import { useCallback, useEffect, useRef, useState } from 'react';
import {
  IconKeyboard,
  IconPlayerPlay,
  IconPlayerStop,
  IconSend,
  IconX,
  IconVideo,
} from '@tabler/icons-react';
import { toast } from 'react-toastify';
import EmojiPicker from 'emoji-picker-react';
import MediaRecorder from 'audio-recorder-polyfill';
import { InterviewType } from '@common/ts/enums';
import { PermissionState } from '@common/media';
import useOnClickOutside from '../../hooks/useClickoutside';
import useSendMessage from '../../hooks/useSendMessage';
import { cn } from '../../lib/utils';
import useChatStore from '../../store';
import { Input } from '../shadcn/input';
import { Toggle } from '../shadcn/toggle';
import 'react-toastify/dist/ReactToastify.css';
import { iframeLocalStorage } from '../../utils/iframeLocalStorage';
import BouncingDots from '../dots-animation/BouncingDots';
import FaceSmileIcon from '../icons/FaceSmileIcon';
import MicrophoneIcon from '../icons/MicrophoneIcon';
import PhotoIcon from '../icons/PhotoIcon';
import useChatSettingsStore from '../../ChatSettingsStore';
import { ChatInputProps } from '../../types';
import {
  translations,
  translateTypeMessage,
  translateCancel,
  translateTapToSend,
  translateTapToStopRecording,
} from '../../translations/chatInputTranslations';

function ChatInput({
  isTestChat,
  setIsVoiceBoxOpen,
  themeData,
  isVideoRecordingPressed,
  isVideoRecordingActive,
  handleOpenVideoModal,
  isVoiceBoxOpen,
  requestCameraPermission,
  requestMicrophonePermission,
  permissions,
  languageCode = 'en',
  onVoiceRecordingTrigger,
}: ChatInputProps) {
  const {
    inputsDisabled,
    setInputsDisabled,
    addMessageAndStartCreateAsistant,
    messages,
    setShowVoiceMainInput,
    showVoiceMainInput,
    currentConversationId,
  } = useChatStore((state) => state);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const { sendMessage, onSendMessageErrorHandler } = useSendMessage();
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [isAudioRecordingPressed, setIsAudioRecordingPressed] = useState(false);
  const [inputState, setInputState] = useState('');
  const [volume, setVolume] = useState<number>(0);
  const requestRef = useRef<number>();
  const volumeRef = useRef<HTMLDivElement>(null);
  const [audioAllowed, setAudioAllowed] = useState<boolean>(true);
  const [audioReadyToSend, setAudioReadyToSend] = useState(false);
  const recorder = useRef<MediaRecorder | null>(null);
  const recorderAudioContext = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioAnalyserRef = useRef<AnalyserNode | null>(null);
  const chunks = useRef<Blob[]>([]);

  const t = translations[languageCode] || translations.en;

  // chat inputs settings from dashboard
  const { chatSettingsData } = useChatSettingsStore();

  const isTextChat = chatSettingsData?.includes(InterviewType.TEXT);
  const isAudioChat = chatSettingsData?.includes(InterviewType.AUDIO);
  const isVideoChat = chatSettingsData?.includes(InterviewType.VIDEO);
  const isImageChat = chatSettingsData?.includes(InterviewType.IMAGE);

  const onlyShowVideoInput = !!isVideoChat && !isAudioChat && !isTextChat;
  const chatSettingsAreEmpty = !isVideoChat && !isAudioChat && !isTextChat;

  const setRecorderAudio = useRef((e: any) => {
    chunks.current.push(e.data);
  });

  // audio
  const stopRecordingChunksAudio = useRef(() => {
    const blob = new Blob(chunks.current, { type: 'audio/wav' });
    const filename = `audio-${Date.now()}.wav`;
    const audioFile = new File([blob], filename, { type: 'audio/wav' });
    addMessageAndStartCreateAsistant({
      _id: Math.random().toString(),
      role: 'user',
      text: '',
      loading: false,
      data: [{ type: InterviewType.AUDIO, url: URL.createObjectURL(blob), isUser: true }],
      created_at: new Date().toISOString(),
    });
    sendMessage({
      file: audioFile,
      text: '',
      onError: onSendMessageErrorHandler,
      onSuccess: () => { chunks.current = []; },
    });
    recorder.current?.stream.getTracks().forEach((track) => track.stop());
    recorderAudioContext.current?.stream.getTracks().forEach((track) => track.stop());
  });

  const getPermissionAudio = async () => {
    if (!navigator?.mediaDevices?.getUserMedia) {
      return toast.error(t.browserMediaSupport);
    }
    if (!isVoiceBoxOpen) {
      recorder.current?.removeEventListener('dataavailable', setRecorderAudio?.current);
      recorder.current?.removeEventListener('stop', stopRecordingChunksAudio?.current);
    }
    try {
      const stream = await navigator?.mediaDevices?.getUserMedia({ audio: true });
      recorder.current = new MediaRecorder(stream);
      recorder.current?.addEventListener('dataavailable', setRecorderAudio?.current);
      recorder.current?.addEventListener('stop', stopRecordingChunksAudio?.current);
    } catch {
      toast.info(t.enableMicrophoneAccess);
      setShowVoiceMainInput(false);
      setAudioAllowed(false);
    }
  };

  const handleClickOutside = useCallback(() => {
    setIsEmojiPickerOpen(false);
  }, []);

  useOnClickOutside(emojiPickerRef, handleClickOutside);

  // audio
  const startRecordingAudio = async (isReady: boolean) => {
    await getPermissionAudio();
    await startAudioContext();
    if (!recorder.current) return;
    try {
      recorder.current.start();
      setIsAudioRecordingPressed(isReady);
    } catch (err) {
      console.error('Error recording audio:', err);
    }
  };

  const stopRecordingAudio = () => {
    if (!recorder.current) return;
    recorder.current?.pause();
    setAudioReadyToSend(true);
    setIsAudioRecordingPressed(false);
  };

  const sendRecordingAudio = () => {
    if (!recorder.current) return;

    // Cancel animation frame if active
    if (requestRef.current) {
      cancelAnimationFrame(requestRef.current);
      requestRef.current = undefined;
    }

    recorder.current?.stop();
    recorderAudioContext.current?.stop();

    // Suspend AudioContext to save resources but don't close it
    if (audioContextRef.current && audioContextRef.current.state === 'running') {
      audioContextRef.current.suspend().catch(console.error);
    }

    setAudioReadyToSend(false);
    setIsAudioRecordingPressed(false);
    setIsVoiceBoxOpen(false);
  };

  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmitText = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const text = e.currentTarget.message.value;
    if (!text) return;
    const newMessage = {
      _id: Math.random().toString(),
      loading: false,
      text,
      data: [],
      role: 'user',
      seen: false,
      created_at: new Date().toISOString(),
    } as TMessageData;
    setInputsDisabled(true);
    addMessageAndStartCreateAsistant(newMessage);
    setInputState('');
    sendMessage({
      text,
      onError: onSendMessageErrorHandler,
    });
  };

  const openVoiceBoxFunction = useCallback(() => {
    if (audioAllowed) {
      setIsVoiceBoxOpen(true);
      removeInputDataOnMessageSent();
    }
  }, [audioAllowed, setIsVoiceBoxOpen]);

  const handleCancelVoiceMessage = () => {
    setIsVoiceBoxOpen(false);

    recorder.current?.stream?.getTracks().forEach((track) => track.stop());
    recorderAudioContext.current?.stream?.getTracks().forEach((track) => track.stop());
    recorder.current?.removeEventListener('dataavailable', setRecorderAudio?.current);
    recorder.current?.removeEventListener('stop', stopRecordingChunksAudio?.current);

    recorder.current?.stop();
    recorderAudioContext.current?.stop();

    setAudioReadyToSend(false);
    setIsAudioRecordingPressed(false);
    setIsVoiceBoxOpen(false);
    chunks.current = [];
  };

  const handleFileupload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'video/mp4'];
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.mp4'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      toast.error('Unsupported file type. Please upload a JPG, PNG, GIF, or MP4 file.');
      e.target.value = '';
      return;
    }

    let messageType: InterviewType.IMAGE | InterviewType.VIDEO | null = null;
    removeInputDataOnMessageSent();
    if (file.type.startsWith(InterviewType.IMAGE)) {
      messageType = InterviewType.IMAGE;
    } else if (file.type.startsWith(InterviewType.VIDEO)) {
      messageType = InterviewType.VIDEO;
    }

    if (!messageType) return;

    const newMessage = {
      _id: Math.random().toString(),
      loading: false,
      text: '',
      data: [{ type: messageType, url: URL.createObjectURL(file), isUser: true }],
      role: 'user',
      seen: false,
      created_at: new Date().toISOString(),
    } as TMessageData;

    setInputsDisabled(true);
    addMessageAndStartCreateAsistant(newMessage);
    sendMessage({
      file,
      text: '',
      onError: onSendMessageErrorHandler,
    });

    e.target.value = '';
  };

  // audio voice volume recognition
  const startAudioContext = async () => {
    try {
      // Only create a new AudioContext if one doesn't already exist
      if (!audioContextRef.current) {
        const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
        audioContextRef.current = new AudioContext();
      } else if (audioContextRef.current?.state === 'suspended') {
        await audioContextRef.current?.resume();
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setAudioAllowed(true);

      if (!audioAnalyserRef.current && audioContextRef.current) {
        audioAnalyserRef.current = audioContextRef.current?.createAnalyser();
        audioAnalyserRef.current.fftSize = 256;
      }

      // Create media stream source and connect to analyzer
      if (audioContextRef.current && audioAnalyserRef.current) {
        const source = audioContextRef.current?.createMediaStreamSource(stream);
        recorderAudioContext.current = new MediaRecorder(stream);
        source?.connect(audioAnalyserRef.current);

        const bufferLength = audioAnalyserRef.current?.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const updateVolume = () => {
          if (audioAnalyserRef.current) {
            audioAnalyserRef.current?.getByteFrequencyData(dataArray);
            const maxVolume = Math.max(...Array.from(dataArray));
            const normalizedVolume = maxVolume / 255;
            setVolume(normalizedVolume);
            requestRef.current = requestAnimationFrame(updateVolume);
          }
        };

        updateVolume();
      }
    } catch (error) {
      setAudioAllowed(false);
    }
  };

  const populateInput = () => {
    const textToAutoPopulate = iframeLocalStorage.getItem('last_user_message');
    setInputState(textToAutoPopulate || '');

    if (textToAutoPopulate) {
      inputRef.current?.focus();
    }
  };

  const removeInputDataOnMessageSent = () => {
    iframeLocalStorage.removeItem('last_user_message');
  };

  const showTextInput = () => {
    setShowVoiceMainInput(false);
  };

  const showVoiceInput = () => {
    setShowVoiceMainInput(true);
  };

  // Handle microphone button click with permission check
  const handleMicrophoneClick = (pressed?: boolean) => {
    if (
      pressed &&
      permissions?.microphone !== PermissionState.GRANTED &&
      requestMicrophonePermission
    ) {
      requestMicrophonePermission();
      return;
    }
    showVoiceInput();
  };

  // Handle video button click with permission check
  const handleVideoClick = () => {
    // Check if we need permissions and not in test chat
    const needsCamera = permissions?.camera !== PermissionState.GRANTED;
    const needsMicrophone =
      chatSettingsData?.includes(InterviewType.AUDIO) &&
      permissions?.microphone !== PermissionState.GRANTED;

    if ((needsCamera || needsMicrophone) && requestCameraPermission && !isTestChat) {
      requestCameraPermission();
      return;
    }

    handleOpenVideoModal();
  };

  // Handle voice recording with permission check
  const handleVoiceRecordingClick = () => {
    if (
      permissions?.microphone !== PermissionState.GRANTED &&
      requestMicrophonePermission &&
      !isTestChat
    ) {
      requestMicrophonePermission();
      return;
    }
    openVoiceBoxFunction();
    startRecordingAudio(true);
  };

  useEffect(() => {
    if (inputRef.current && !inputsDisabled && !showVoiceMainInput) {
      inputRef.current.focus();
    }
  }, [showVoiceMainInput]);

  useEffect(() => {
    populateInput();
  }, [messages]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      iframeLocalStorage.removeItem('last_user_message');
      setInputState('');
    };

    const handleUnload = () => {
      iframeLocalStorage.removeItem('last_user_message');
      setInputState('');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    window.addEventListener('unload', handleUnload);

    // Clean up audio related resources
    if (requestRef.current) {
      cancelAnimationFrame(requestRef.current);
    }

    // Stop all tracks
    recorder.current?.stream?.getTracks().forEach((track) => track.stop());
    recorderAudioContext.current?.stream?.getTracks().forEach((track) => track.stop());

    // Close AudioContext to release system audio resources
    if (audioContextRef.current) {
      audioContextRef.current?.close().catch((error) => {
        toast.error(`Error closing audio: ${error.message}`);
      });
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
  }, []);

  useEffect(() => {
    setIsVoiceBoxOpen(false);
  }, [audioAllowed]);

  const circleWidth = `${80 + volume * 230}px`;

  useEffect(() => {
    const firstToShowMicOrTextInput = !!isAudioChat;
    setShowVoiceMainInput(firstToShowMicOrTextInput);
  }, [setShowVoiceMainInput, chatSettingsData]);

  useEffect(() => {
    if (!inputsDisabled && !showVoiceMainInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showVoiceMainInput, inputsDisabled, inputRef]);

  // Expose voice recording trigger function to parent
  useEffect(() => {
    if (onVoiceRecordingTrigger) {
      onVoiceRecordingTrigger(() => {
        openVoiceBoxFunction();
        startRecordingAudio(true);
      });
    }
  }, [onVoiceRecordingTrigger, openVoiceBoxFunction, startRecordingAudio]);

  return (
    <form
      onSubmit={handleSubmitText}
      className={cn(
        isTestChat
          ? 'relative'
          : 'max-w-[997px] mx-auto min500:bg-white fixed flex-col bottom-0 left-0 right-0',
        'flex items-center w-full',
        isVoiceBoxOpen && 'max500:px-2',
      )}
    >
      {!showVoiceMainInput && (
        <div
          className={cn(
            'flex w-full gap-2.5',
            !isTestChat && 'px-2 pb-3 min500:bg-white',
            isVoiceBoxOpen && 'bg-white opacity-0',
            chatSettingsAreEmpty && 'justify-end',
          )}
        >
          {/* Text input */}
          {isTextChat && (
            <div
              className={cn(
                'flex py-1.5 w-full border-2 px-2.5 rounded-full shadow bg-white',
                inputsDisabled ||
                  isVideoRecordingActive ||
                  isAudioRecordingPressed ||
                  isVoiceBoxOpen
                  ? '!bg-gray-200'
                  : '',
                !isTestChat && 'mt-2.5',
              )}
            >
              {isEmojiPickerOpen && (
                <div ref={emojiPickerRef} className={cn('absolute bottom-full left-0')}>
                  <EmojiPicker
                    onEmojiClick={(selectedEmoji: { emoji: any }) =>
                      setInputState((prev) => `${prev}${selectedEmoji.emoji}`)
                    }
                    className="w-full"
                    lazyLoadEmojis
                    skinTonesDisabled
                    searchDisabled
                  />
                </div>
              )}
              <button
                disabled={inputsDisabled || isVideoRecordingActive}
                onClick={() => setIsEmojiPickerOpen((prev) => !prev)}
                type="button"
              >
                <FaceSmileIcon className="text-gray-500 size-6" />
              </button>
              <Input
                value={inputState}
                onChange={(e) => setInputState(e.target.value)}
                className="text-base bg-white border-none shadow-none outline-none"
                type="text"
                autoComplete="off"
                name="message"
                disabled={
                  inputsDisabled ||
                  isVideoRecordingActive ||
                  isAudioRecordingPressed ||
                  isVoiceBoxOpen
                }
                placeholder={translateTypeMessage[languageCode] || translateTypeMessage.en}
                ref={inputRef}
              />
            </div>
          )}

          {isTextChat && inputState ? (
            <button
              type="submit"
              disabled={inputsDisabled}
              onClick={removeInputDataOnMessageSent}
              style={{ backgroundColor: themeData?.['chat-primary'] || '#FF6F98' }}
              className={cn(
                'mt-2 flex items-center rounded-full justify-center text-white transition-all ease-in-out min-w-[45px] w-[45px] h-[45px] disabled:opacity-50',
                isTestChat ? 'mt-2' : 'mt-4',
              )}
            >
              <IconSend size={24} />
            </button>
          ) : (
            <div
              className={cn(
                'flex items-center justify-center gap-1 mt-4',
                isTestChat ? 'mt-0' : 'mt-4',
                onlyShowVideoInput && 'flex-row-reverse w-full gap-2',
              )}
            >
              {/* image upload */}
              {isImageChat && (
                <label
                  style={{ backgroundColor: themeData?.['chat-primary'] || '#FF6F98' }}
                  className={cn(
                    (inputsDisabled || isVideoRecordingPressed || isAudioRecordingPressed) &&
                      'opacity-50',
                    'flex items-center justify-center cursor-pointer p-2 text-white transition-all ease-in-out rounded-full min500:mt-px',
                  )}
                >
                  <input
                    disabled={inputsDisabled || isVideoRecordingPressed || isAudioRecordingPressed}
                    onChange={handleFileupload}
                    accept=".jpg, .jpeg, .mp4, .png, .gif"
                    className="hidden"
                    type="file"
                    name=""
                    id=""
                  />
                  <PhotoIcon className="text-white size-6" />
                </label>
              )}
              {isAudioChat && (
                <Toggle
                  pressed={isVoiceBoxOpen}
                  onPressedChange={handleMicrophoneClick}
                  variant="outline"
                  style={{ backgroundColor: themeData?.primary || '#096846' }}
                  className={cn(
                    'relative p-2 text-white rounded-full hover:text-white hover:shadow',
                  )}
                >
                  <MicrophoneIcon
                    className={cn(isAudioRecordingPressed && 'animate-pulse', 'size-6')}
                  />
                </Toggle>
              )}

              {isVideoChat && (
                <button
                  onClick={handleVideoClick}
                  disabled={inputsDisabled || isVideoRecordingPressed || isAudioRecordingPressed}
                  style={{ backgroundColor: themeData?.['chat-primary'] || '#FF6F98' }}
                  type="button"
                  className={cn(
                    'flex items-center justify-center p-2 px-2 text-white transition-all ease-in-out rounded-full disabled:opacity-80 min500:mt-px',
                    onlyShowVideoInput && 'w-full h-[50px]',
                  )}
                >
                  <IconVideo size={24} />
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {showVoiceMainInput && currentConversationId && (
        <div
          className={cn(
            'items-center w-full min500:bg-white min500:pt-6 min500:mb-6 gap-1 mb-3.5 h-[50px]',
            isVoiceBoxOpen && audioAllowed ? 'hidden' : 'flex',
            !isTestChat && 'px-2',
            onlyShowVideoInput && 'flex-row-reverse w-full gap-2',
            chatSettingsAreEmpty && 'justify-end',
          )}
        >
          {/* Voice input */}
          {isAudioChat && (
            <button
              onClick={handleVoiceRecordingClick}
              type="button"
              disabled={inputsDisabled || isVideoRecordingPressed || !audioAllowed}
              style={{ backgroundColor: themeData?.primary || '#096846' }}
              className="disabled:opacity-80 flex items-center justify-center w-full h-[50px] px-4 text-white transition-all ease-in-out rounded-full"
            >
              {inputsDisabled ? <BouncingDots /> : <MicrophoneIcon className="size-6" />}
            </button>
          )}

          {/* image upload */}
          {isImageChat && (
            <label
              style={{ backgroundColor: themeData?.['chat-primary'] || '#FF6F98' }}
              className={cn(
                (inputsDisabled || isVideoRecordingPressed || isAudioRecordingPressed) &&
                  'opacity-80',
                'flex items-center justify-center cursor-pointer p-2 px-2 mt-1 text-white transition-all ease-in-out rounded-full min500:mt-px',
              )}
            >
              <input
                disabled={inputsDisabled || isVideoRecordingPressed || isAudioRecordingPressed}
                onChange={handleFileupload}
                accept=".jpg, .jpeg, .mp4, .png, .gif"
                className="hidden"
                type="file"
                name=""
                id=""
              />
              <PhotoIcon className="text-white size-6" />
            </label>
          )}
          {isTextChat && (
            <button
              onClick={showTextInput}
              disabled={inputsDisabled || isVideoRecordingPressed || isAudioRecordingPressed}
              style={{ backgroundColor: themeData?.['chat-primary'] || '#FF6F98' }}
              type="button"
              className="flex items-center justify-center p-2 px-2 mt-1 text-white transition-all ease-in-out rounded-full disabled:opacity-80 min500:mt-px"
            >
              <IconKeyboard size={24} />
            </button>
          )}

          {isVideoChat && (
            <button
              onClick={handleVideoClick}
              disabled={inputsDisabled || isVideoRecordingPressed || isAudioRecordingPressed}
              style={{ backgroundColor: themeData?.['chat-primary'] || '#FF6F98' }}
              type="button"
              className={cn(
                'flex items-center justify-center p-2 px-2 mt-1 text-white transition-all ease-in-out rounded-full disabled:opacity-80 min500:mt-px',
                onlyShowVideoInput && 'w-full h-[50px]',
              )}
            >
              <IconVideo size={24} />
            </button>
          )}
        </div>
      )}

      {/* voice recording box */}
      {isAudioChat && audioAllowed && (
        <div
          className={cn(
            'absolute rounded-b-xl left-0 right-0 w-full h-fit min500:ml-0 transition-all ease-in-out duration-300',
            isVoiceBoxOpen && audioAllowed
              ? isTestChat
                ? '-bottom-12'
                : '-bottom-0'
              : '-bottom-[50vh]',
            !isTestChat && 'ml-[0.4rem]',
          )}
        >
          <div
            style={{ backgroundColor: themeData?.['chat-primary'] || '#FF6F98' }}
            className={cn(
              'overflow-hidden max-w-[984px] min500:w-full max500:mb-4 rounded-b-2xl relative h-[40vh] items-center justify-center w-full flex text-white',
              !isTestChat && 'min500:rounded-b-none lg1000:w-[98.2%] md800:w-[97.7%]',
            )}
          >
            {/* close box - open voice button */}
            {!isAudioRecordingPressed && (
              <button
                onClick={handleCancelVoiceMessage}
                type="button"
                className="absolute z-50 p-1.5 text-white bg-white rounded-full top-5 right-4"
              >
                <IconX size={16} className="text-black" />
              </button>
            )}

            {/* white voice circle */}
            {isAudioRecordingPressed && (
              <div className="absolute">
                <div>
                  <div
                    ref={volumeRef}
                    className={cn(
                      'absolute translate-x-1/2 -translate-y-1/2 rounded-full bg-white/30 top-1/2 right-1/2',
                    )}
                    style={{ width: circleWidth, height: circleWidth }}
                  ></div>
                </div>
              </div>
            )}

            {/* stop and play buttons */}
            {!inputsDisabled && !audioReadyToSend && isAudioRecordingPressed && (
              <button
                type="button"
                onClick={stopRecordingAudio}
                className="z-30 flex items-center justify-center gap-2 text-lg font-medium text-center cursor-pointer"
              >
                <span className="p-1 text-white border border-2 rounded-full">
                  <IconPlayerStop size={28} />
                </span>
                {translateTapToStopRecording[languageCode] || translateTapToStopRecording.en}
              </button>
            )}
            {/* record again or cancel */}
            {!inputsDisabled && audioReadyToSend && (
              <div className="relative items-center justify-center w-full h-full">
                <button
                  type="button"
                  onClick={sendRecordingAudio}
                  className="absolute z-30 flex items-center justify-center gap-2 text-lg font-medium -translate-x-1/2 -translate-y-1/2 cursor-pointer top-1/2 left-1/2"
                >
                  <span className="p-1 text-white border border-2 rounded-full">
                    <IconPlayerPlay size={28} />
                  </span>
                  {translateTapToSend[languageCode] || translateTapToSend.en}
                </button>
                <div className="absolute flex items-center gap-5 -translate-x-1/2 bottom-6 left-1/2">
                  <button type="button" onClick={handleCancelVoiceMessage}>
                    {translateCancel[languageCode] || translateCancel.en}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </form>
  );
}

export default ChatInput;
